import Store, { action } from '../store';
import { ISession, ISessionData, IChartSeries } from 'types';
import { SIGN_OUT_SUCCESS } from '../auth';
import {
  UPDATE_SESSION_LIST,
  WATCH_SESSION,
  UPDATE_SESSION,
  SET_SESSION_TIMESTAMP,
  UPDATE_SESSION_DATA,
  UPDATE_SESSION_ATTR
} from './constants';

import math from 'mathjs';
import { getMinMax, calculatePointAt } from 'helpers/chart';
import { debounce } from 'helpers/decorators';

function findClosestTimestampIndex(points, timestamp) {
  let i = points.length;
  while (i--) {
    if (points[i][0] < timestamp) return i;
  }
  return 0;
}

function sort(s1: ISession, s2: ISession) {
  return s1.created < s2.created ? 1 : -1;
}

export default class SessionStore extends Store {
  list: ISession[] = [];
  inspect: ISessionData = {};
  inspectDataKey: String;
  hasInspected: Boolean = false;
  inspectData: { [string]: any };
  loadedList = false;
  activeSession: string;
  activeTimestamp: number = 0;

  series: IChartSeries[] = [];

  attrs: { [key: string]: IChartSeries } = {};

  get(sessionKey: string = this.activeSession) {
    if (sessionKey) return this.list.find(s => s.key === sessionKey);
  }

  getTimestampIndex(attr: string, timestamp = this.activeTimestamp): number {
    const { data = {} } = this.get() || {};
    return data[attr] ? findClosestTimestampIndex(data[attr], timestamp) : 0;
  }

  @debounce(500)
  updateSeries(session: ISession) {
    if (!session) return;
    const series: IChartSeries[] = (this.series = []);
    const data = session.fullData;
    const keys = Object.keys(data || {});
    for (const id in data) {
      const attr = this.attrs[id];
      if (!attr || !attr.hide) series.push({ ...attr, id, data: data[id] || [] });
    }

    for (const id in this.attrs) {
      const attr = this.attrs[id];
      if (attr.custom && attr.merge) {
        const points = [];

        const mergeSeries = keys.filter(id => attr.merge.search(id) !== -1);
        if (mergeSeries.length) {
          let range = [Infinity, -Infinity],
            len = 0;
          for (const id of mergeSeries) {
            const points = data[id];
            const seriesRange = getMinMax(points);
            if (seriesRange[0] < range[0]) range[0] = seriesRange[0];
            if (seriesRange[1] > range[1]) range[1] = seriesRange[1];
            if (points.length > len) len = points.length;
          }

          try {
            const mergeFunc = math.compile(attr.merge);
            const mergeScope = {};
            const totalTime = range[1] - range[0];
            for (let i = 0; i < len; i++) {
              let time = (i / len) * totalTime + range[0];
              for (let id of mergeSeries) mergeScope[id] = calculatePointAt(time, data[id]);
              points.push([time, mergeFunc.eval(mergeScope)]);
            }
          } catch (e) {
            console.log('Missing attribute');
          }
        }

        series.push({
          ...attr,
          id,
          data: points
        });
      }
    }

    this.emit();
  }

  @action(WATCH_SESSION)
  handleWatch(e, sessionKey) {
    this.activeTimestamp = 0;
    this.activeSession = sessionKey;
    this.emit();
  }

  @action(UPDATE_SESSION)
  handleUpdate(e, update: ISession) {
    this.list = Store.updateOrAdd(this.list, update).sort(sort);
    this.emit();
  }

  @action(UPDATE_SESSION_DATA)
  handleSessionData(e, update) {
    const session = { key: update.key, ...this.get(update.key) };
    session.fullData = { ...session.fullData, ...update.fullData };
    if (update.gps) session.gps = update.gps;
    this.updateSeries(session);
    this.list = Store.addOrReplace(this.list, session).sort(sort);
    this.emit();
  }

  @action(SET_SESSION_TIMESTAMP)
  handleTimestamp(e, timestamp: number) {
    this.activeTimestamp = timestamp;
    this.emit();
  }

  @action(UPDATE_SESSION_LIST)
  handleList(e, list) {
    this.loadedList = true;
    this.list = list.sort(sort);
    this.emit();
  }

  @action(UPDATE_SESSION_ATTR)
  handleUpdateAttr(e, attr) {
    const id = attr.key;
    delete attr.key;
    this.attrs = {
      ...this.attrs,
      [id]: attr
    };
    this.updateSeries(this.get());
    this.emit();
  }

  @action(SIGN_OUT_SUCCESS)
  handleSignout() {
    this.activeSession = 0;
    this.activeTimestamp = 0;
    this.list = [];
    this.inspect = {};
    this.emit();
  }
}
